/**
 * Product Carousel Logic
 *
 * This script handles the functionality of the responsive product carousel.
 * - It manages the sliding behavior based on screen size.
 * - It updates the state of the navigation buttons (enabled/disabled).
 * - It recalculates dimensions on window resize to ensure responsiveness.
 * - It includes an auto-rotation feature that pauses on hover.
 */
document.addEventListener("DOMContentLoaded", function () {
  const container = document.querySelector(".product-carousel-container");
  if (!container) {
    console.error("Carousel container not found. Exiting.");
    return;
  }
  const track = container.querySelector(".product-carousel-track");
  if (!track) {
    console.error("Carousel track not found. Exiting.");
    return;
  }

  const nextButton = container.querySelector(".next-btn");
  const prevButton = container.querySelector(".prev-btn");

  let baseSlides = Array.from(track.children);
  if (baseSlides.length === 0) return;

  let itemsVisible = getItemsVisible();
  let currentIndex = 0;
  let autoRotateInterval;
  const autoRotateDelay = 3000; // faster auto-rotate
  let slideWidth = 0;
  let isAnimating = false;
  let resizeTimer = 0;

  function getItemsVisible() {
    return window.matchMedia("(min-width: 768px)").matches ? 4 : 2;
  }

  function measureSlideWidth() {
    // Prefer bounding rect; fallback to offsetWidth
    const reference = track.querySelector(".product-slide");
    if (!reference) return 0;
    const rectWidth = reference.getBoundingClientRect().width;
    return rectWidth || reference.offsetWidth || 0;
  }

  function ensureSlideWidth(maxAttempts, delayMs, onReady) {
    let attempts = 0;
    function tryMeasure() {
      slideWidth = measureSlideWidth();
      if (slideWidth > 0 || attempts >= maxAttempts) {
        onReady();
        return;
      }
      attempts++;
      setTimeout(tryMeasure, delayMs);
    }
    tryMeasure();
  }

  function setTransform(index, withTransition) {
    const x = -index * slideWidth;
    if (withTransition) {
      track.classList.add("is-animating");
      // Force reflow so the transition is applied consistently
      void track.offsetWidth;
      track.style.transform = `translate3d(${x}px, 0, 0)`;
    } else {
      track.classList.remove("is-animating");
      track.style.transform = `translate3d(${x}px, 0, 0)`;
    }
  }

  function buildInfiniteClones() {
    // Clean previous clones if any
    track.innerHTML = "";
    const fragment = document.createDocumentFragment();

    const visible = itemsVisible;
    const total = baseSlides.length;
    if (total === 0) return;

    // Clone tail -> prepend
    for (let i = total - visible; i < total; i++) {
      fragment.appendChild(baseSlides[i].cloneNode(true));
    }
    // Original
    baseSlides.forEach((s) => fragment.appendChild(s.cloneNode(true)));
    // Clone head -> append
    for (let i = 0; i < visible; i++) {
      fragment.appendChild(baseSlides[i].cloneNode(true));
    }

    track.appendChild(fragment);

    // Update references
    currentIndex = visible; // start at first real slide
    ensureSlideWidth(10, 50, () => {
      setTransform(currentIndex, false);
    });
  }

  function updateButtons() {
    // In infinite mode, buttons are always enabled
    if (prevButton) prevButton.disabled = false;
    if (nextButton) nextButton.disabled = false;
  }

  function moveBy(delta) {
    if (isAnimating) return;
    isAnimating = true;
    currentIndex += delta;
    setTransform(currentIndex, true);
  }

  function handleTransitionEnd() {
    isAnimating = false;
    const total = baseSlides.length;
    const visible = itemsVisible;
    const maxIndex = visible + total - 1;

    if (currentIndex > maxIndex) {
      currentIndex = visible; // wrap to first real
      setTransform(currentIndex, false);
    } else if (currentIndex < visible) {
      currentIndex = visible + total - 1; // wrap to last real
      setTransform(currentIndex, false);
    }
  }

  function moveToNextSlide() {
    moveBy(1);
  }

  function moveToPrevSlide() {
    moveBy(-1);
  }

  function startAutoRotate() {
    stopAutoRotate();
    autoRotateInterval = setInterval(moveToNextSlide, autoRotateDelay);
  }

  function stopAutoRotate() {
    clearInterval(autoRotateInterval);
  }

  // Buttons
  if (nextButton) {
    nextButton.addEventListener("click", () => {
      moveToNextSlide();
      startAutoRotate();
    });
  }

  if (prevButton) {
    prevButton.addEventListener("click", () => {
      moveToPrevSlide();
      startAutoRotate();
    });
  }

  // Resize handling: debounce and rebuild safely
  window.addEventListener("resize", () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      stopAutoRotate();
      itemsVisible = getItemsVisible();
      // Wait a frame for layout to stabilize before rebuilding
      requestAnimationFrame(() => {
        buildInfiniteClones();
        // Restart after transform is applied
        setTimeout(startAutoRotate, 100);
      });
    }, 150);
  });

  // Hover to pause
  container.addEventListener("mouseenter", stopAutoRotate);
  container.addEventListener("mouseleave", startAutoRotate);

  // Touch/Swipe support
  let startX = 0;
  let startY = 0;
  let isSwiping = false;
  let isHorizontalSwipe = false;

  track.addEventListener("touchstart", function (e) {
    if (e.touches.length === 1) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isSwiping = true;
      isHorizontalSwipe = false;
    }
  });

  track.addEventListener(
    "touchmove",
    function (e) {
      if (!isSwiping) return;

      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = Math.abs(currentX - startX);
      const diffY = Math.abs(currentY - startY);

      // Determine direction on first significant movement
      if (!isHorizontalSwipe && (diffX > 10 || diffY > 10)) {
        isHorizontalSwipe = diffX > diffY;
      }

      // Only prevent default for horizontal swipes
      if (isHorizontalSwipe) {
        e.preventDefault();
      }
    },
    { passive: false }
  );

  track.addEventListener("touchend", function (e) {
    if (!isSwiping) return;

    // Only process horizontal swipes for carousel navigation
    if (isHorizontalSwipe) {
      const endX = e.changedTouches[0].clientX;
      const diffX = endX - startX;
      const threshold = 40;
      if (diffX > threshold) {
        moveToPrevSlide();
        startAutoRotate();
      } else if (diffX < -threshold) {
        moveToNextSlide();
        startAutoRotate();
      }
    }

    isSwiping = false;
    isHorizontalSwipe = false;
  });

  // Transition end to snap seamlessly when crossing clones
  track.addEventListener("transitionend", handleTransitionEnd);

  // Initial setup after layout
  setTimeout(() => {
    buildInfiniteClones();
    updateButtons();
    startAutoRotate();
  }, 100);
});
